import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { DefaultOrgIcon } from '~assets/icons'
import { type RootState } from '~redux/rootReducer'
import { selectWorkspaceOrgs, setSelectedEIOrganization } from '~redux/slices/organizationSlice'

import Avatar from '../Avatar'
import Dropdown, { type DropdownOption } from '../Dropdown'
import OrganizationChip from '../OrganizationChip'

// Ordered most-specific first so the navigate-after-org-change logic picks the right subpath
const AI_NAVIGATE_PATHS = [
  'sources/links',
  'sources/domains',
  'sources/documents',
  'sources/faqs',
  'sources/processes',
  'sources/transfer-lines',
  'sources/gis-layers',
  'sources/integrations',
  'sources',
  'ai-customize',
  'ai-log',
  'analytics',
  'health-check',
]

export interface AIOrgSelectorProps {
  searchable?: boolean
  searchThreshold?: number
  label?: string
}

const AIOrgSelector: React.FC<AIOrgSelectorProps> = ({
  searchable,
  searchThreshold = 10,
  label = 'Organization',
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const orgs = useSelector(selectWorkspaceOrgs)
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )

  const options: DropdownOption[] = useMemo(
    () =>
      orgs.map((org) => {
        const logoSrc = org.image ?? org.workspace_icon
        return {
          value: org.id,
          label: org.name ?? '',
          avatar: logoSrc ? (
            <Avatar size='sm' src={logoSrc} alt={org.name ?? 'Organization'} />
          ) : (
            <DefaultOrgIcon width={20} height={20} />
          ),
        }
      }),
    [orgs],
  )

  if (orgs.length === 0) return null

  if (orgs.length === 1) {
    const only = orgs[0]
    return (
      <OrganizationChip
        orgName={only.name ?? ''}
        orgIcon={only.image ?? only.workspace_icon ?? undefined}
        size='medium'
      />
    )
  }

  const handleChange = (next: string | string[]): void => {
    const id = Array.isArray(next) ? next[0] : next
    const org = orgs.find((o) => o.id === id)
    if (!org?.handle || org.handle === selectedOrganization?.handle) return

    const previousHandle = selectedOrganization?.handle
    dispatch(setSelectedEIOrganization(org))
    if (location.pathname.includes(`/organizations/${org.handle}/`)) return
    const currentPage = AI_NAVIGATE_PATHS.find((page) =>
      location.pathname.includes(`/organizations/${previousHandle}/${page}`),
    )
    navigate(
      currentPage
        ? `/organizations/${org.handle}/${currentPage}${location.search}`
        : `/organizations/${org.handle}/sources`,
    )
  }

  return (
    <Dropdown
      label={label}
      options={options}
      value={selectedOrganization?.id}
      onChange={handleChange}
      placeholder='Select organization'
      searchable={searchable}
      searchThreshold={searchThreshold}
      fullWidth={false}
      containerSx={{ minWidth: 300, maxWidth: 400, width: 'fit-content' }}
    />
  )
}

export default AIOrgSelector
